import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import type { CaseDetail, ModelSummaryResponse } from '@/services/projections'
import { downloadJsonFile } from '@/utils/downloadFile'
import { todayIsoDate } from '@/utils/waterfallCalculator'
import type { History, SchemaEntry } from './engine/types'
import { buildTemplate, serializeCase } from './caseJson'
import { caseNameSlug } from './caseJsonSlug'

type CaseJsonMenuProps = {
  schema: SchemaEntry[]
  history: History | null
  model: ModelSummaryResponse | null
  selectedCase: CaseDetail | null
  saved: import('./engine/types').CaseInputs
  notes: Record<string, string>
  dirty: boolean
  pageCanEdit: boolean
  onNew: () => void
  onUploadJson: () => void
}

function basisMonth(lastClosedMonth: string | null | undefined): string | null {
  if (!lastClosedMonth) return null
  return lastClosedMonth.length >= 7 ? lastClosedMonth.slice(0, 7) : lastClosedMonth
}

export default function CaseJsonMenu({
  schema,
  history,
  model,
  selectedCase,
  saved,
  notes,
  dirty,
  pageCanEdit,
  onNew,
  onUploadJson,
}: CaseJsonMenuProps) {
  const [menuOpen, setMenuOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const importReady = model?.latest_import != null
  const month = basisMonth(model?.latest_import?.last_closed_month)
  const fingerprint = model?.schema_fingerprint ?? ''
  const lastClosed = model?.latest_import?.last_closed_month ?? ''

  useEffect(() => {
    if (!menuOpen) return
    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node)) return
      setMenuOpen(false)
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [menuOpen])

  const templateDisabled = !importReady
  const currentDisabled = !importReady || dirty || selectedCase == null

  const menuItems = useMemo(
    () => [
      {
        id: 'current',
        label: 'Download current JSON',
        disabled: currentDisabled,
        onSelect: () => {
          if (!month || !selectedCase) return
          const file = serializeCase({
            schema,
            caseMeta: {
              name: selectedCase.name,
              description: selectedCase.description ?? undefined,
              expires_on: selectedCase.expires_on,
            },
            inputs: saved,
            notes,
            lastClosedMonth: lastClosed,
            schemaFingerprint: fingerprint,
          })
          downloadJsonFile(
            `projection-case_${caseNameSlug(selectedCase.name)}_${todayIsoDate()}.json`,
            file,
          )
        },
      },
      ...(pageCanEdit
        ? [
            {
              id: 'upload',
              label: 'Import from template',
              disabled: false,
              onSelect: () => {
                onUploadJson()
              },
            },
          ]
        : []),
      {
        id: 'blank',
        label: 'Download blank template',
        disabled: templateDisabled,
        onSelect: () => {
          if (!month) return
          const file = buildTemplate({
            schema,
            lastClosedMonth: lastClosed,
            schemaFingerprint: fingerprint,
          })
          downloadJsonFile(`projection-case-template_${month}.json`, file)
        },
      },
      {
        id: 'actuals',
        label: 'Download template with actuals (24 months)',
        disabled: templateDisabled || history == null,
        onSelect: () => {
          if (!month || !history) return
          const file = buildTemplate({
            schema,
            lastClosedMonth: lastClosed,
            schemaFingerprint: fingerprint,
            history,
            historyMonths: 24,
          })
          downloadJsonFile(`projection-case-template-with-actuals_${month}.json`, file)
        },
      },
    ],
    [
      currentDisabled,
      fingerprint,
      history,
      lastClosed,
      month,
      notes,
      onUploadJson,
      pageCanEdit,
      saved,
      schema,
      selectedCase,
      templateDisabled,
    ],
  )

  return (
    <div className="relative" ref={containerRef}>
      <div
        className={[
          'flex h-8 shrink-0 items-stretch overflow-hidden rounded-lg text-xs font-medium text-white shadow-sm',
          '[background:var(--gradient-primary)]',
          !pageCanEdit ? 'cursor-not-allowed opacity-50' : '',
        ].join(' ')}
      >
        <button
          type="button"
          disabled={!pageCanEdit}
          className="inline-flex items-center px-3 hover:bg-black/10 disabled:cursor-not-allowed"
          onClick={onNew}
        >
          New
        </button>
        <button
          type="button"
          disabled={!pageCanEdit}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label="More new case actions"
          className="flex w-8 items-center justify-center border-l border-white/25 hover:bg-black/10 disabled:cursor-not-allowed"
          onClick={(event) => {
            event.stopPropagation()
            if (!pageCanEdit) return
            setMenuOpen((open) => !open)
          }}
        >
          <ChevronDownIcon
            className={`h-4 w-4 transition-transform ${menuOpen ? 'rotate-180' : ''}`}
            aria-hidden
          />
        </button>
      </div>
      {menuOpen ? (
        <div
          role="menu"
          className="absolute left-0 z-30 mt-1 min-w-[14rem] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
        >
          {menuItems.map((item) => (
            <button
              key={item.id}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              className="flex w-full items-center px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-300"
              onClick={() => {
                if (item.disabled) return
                setMenuOpen(false)
                item.onSelect()
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}
